<!--
  ApplicationEdit.svelte — Dedicated admin page for editing a single application.

  Replaces the crowded edit-application modal. Each section of application
  management gets its own card for clarity: basic info, URLs, IP restrictions,
  token limits, toggles, and media uploads.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Input,
		Spinner,
		Toggle,
		Label,
		Textarea,
		Badge
	} from 'flowbite-svelte';
	import { push } from '../../abstract-spa-router';
	import PageHeader from '../../components/PageHeader.svelte';
	import { ConfirmButton, FormField } from '../../components';
	import {
		getApplication,
		updateApplication,
		uploadApplicationLogo,
		deleteApplicationLogo,
		uploadApplicationIcon,
		deleteApplicationIcon
	} from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import site from '../../site';
	import type { Application, ApplicationDownloadGroup, ApplicationLink } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route param injected by abstract-spa-router. */
		params?: { id?: string };
	}

	let { params = {} }: Props = $props();

	// ── State ─────────────────────────────────────────────────────

	/** The application being edited. */
	let app: Application | null = $state(null);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error text for the initial load. */
	let error = $state('');
	/** Whether a save is in progress. */
	let isSaving = $state(false);

	/** API base URL for constructing image URLs. */
	const apiBase = site.backend.baseURL;

	// ── Form fields ───────────────────────────────────────────────

	let formName = $state('');
	let formDescription = $state('');
	let formWebsite = $state('');
	let formVerificationURLs = $state('');
	let formIcon = $state('');
	let formIsActive = $state(true);
	let formIsAlwaysVisible = $state(false);
	let formWhenRegistered = $state(false);
	let formIsAutomaticApprovalEnabled = $state(false);
	let formIsTokenFlowEnabled = $state(false);
	let formWhitelistedIPs = $state('');
	let formBlacklistedIPs = $state('');
	let formNonRegisteredURL = $state('');
	/** Whether this application is backed by an external licensing backend. */
	let formIsLicensingEnabled = $state(false);
	/** Base URL of that backend, including its version prefix. */
	let formLicenseBackendURL = $state('');
	/** Whether users may re-assign seats of their own licences from Abstract ID. */
	let formIsSeatManagementEnabled = $state(true);
	/** What to call the application on the licence pages. Blank uses the registry name. */
	let formLicenseDisplayName = $state('');
	/** Where a user holding no licence for this product is offered to go. */
	let formLicensePurchaseURL = $state('');
	/**
	 * One documentation link while it is being edited.
	 *
	 * The optional halves are empty strings rather than `null` here: an `Input`
	 * binds to a string, and a `null` would show the literal word in the field.
	 * They become `null` again on save.
	 */
	interface LinkDraft {
		/** Link text. */
		name: string;
		/** Font Awesome class. */
		icon: string;
		/** One line explaining where the link goes. */
		description: string;
		/** Absolute destination URL. */
		url: string;
	}

	/** Documentation and support links shown beneath a user's licences. */
	let formDocumentationLinks = $state<LinkDraft[]>([]);
	/** Display names for the download groups, in the order they should appear. */
	let formDownloadGroups = $state<ApplicationDownloadGroup[]>([]);
	/**
	 * What to call the bucket for builds the backend files under no group.
	 *
	 * Held apart from {@link formDownloadGroups} because it has no identifier to
	 * type — it is stored under the reserved empty key, which the editor should
	 * never ask anyone to know about.
	 */
	let formUngroupedName = $state('');

	// ── Upload state ──────────────────────────────────────────────

	/** Whether a logo upload is in progress. */
	let uploadingLogo = $state(false);
	/** Whether an icon upload is in progress. */
	let uploadingIcon = $state(false);

	// ── Data loading ──────────────────────────────────────────────

	/** Populate form fields from a loaded application. */
	function populateForm(a: Application): void {
		formName = a.applicationName;
		formDescription = a.description || '';
		formWebsite = a.website || '';
		formVerificationURLs = (a.verificationURLs || []).join('\n');
		formIcon = a.icon || '';
		formIsActive = a.isActive;
		formIsAlwaysVisible = a.isAlwaysVisible;
		formWhenRegistered = a.whenRegistered;
		formIsAutomaticApprovalEnabled = a.isAutomaticApprovalEnabled;
		formIsTokenFlowEnabled = a.isTokenFlowEnabled;
		formWhitelistedIPs = a.whitelistedIPs || '';
		formBlacklistedIPs = a.blacklistedIPs || '';
		formNonRegisteredURL = a.nonRegisteredURL || '';
		formIsLicensingEnabled = a.isLicensingEnabled;
		formLicenseBackendURL = a.licenseBackendURL || '';
		formIsSeatManagementEnabled = a.isSeatManagementEnabled;
		formLicenseDisplayName = a.licenseDisplayName || '';
		formLicensePurchaseURL = a.licensePurchaseURL || '';
		formDownloadGroups = (a.downloadGroups ?? []).filter((group) => group.key).map((group) => ({ ...group }));
		formUngroupedName = (a.downloadGroups ?? []).find((group) => !group.key)?.name ?? '';
		/* Copied, not referenced: the editor mutates entries in place and the loaded
		   application is what an unsaved edit is compared against. */
		formDocumentationLinks = (a.documentationLinks ?? []).map((link) => ({
			name: link.name,
			icon: link.icon ?? '',
			description: link.description ?? '',
			url: link.url
		}));
	}

	/** Load the application by ID and populate the form. */
	async function loadApplication(): Promise<void> {
		const id = parseInt(params.id || '', 10);
		/* Bail early if the route param is not a valid numeric ID. */
		if (isNaN(id)) {
			error = t2('Invalid application ID.');
			loading = false;
			return;
		}
		/* Fetch the application and populate all form fields. */
		try {
			app = await getApplication(id);
			populateForm(app);
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load application');
		} finally {
			loading = false;
		}
	}

	// ── Save ──────────────────────────────────────────────────────

	/** Save the application fields. */
	async function handleSave(): Promise<void> {
		if (!app) return;
		/* Require a non-empty application name before saving. */
		if (!formName.trim()) {
			toastError(t2('Application name is required.'));
			return;
		}

		isSaving = true;
		/* Send the updated fields to the server and navigate back on success. */
		try {
			await updateApplication(app.id, {
				applicationName: formName.trim(),
				description: formDescription || null,
				website: formWebsite || null,
				verificationURLs: formVerificationURLs
					.split('\n')
					.map((u) => u.trim())
					.filter((u) => u.length > 0),
				icon: formIcon || null,
				isActive: formIsActive,
				isAlwaysVisible: formIsAlwaysVisible,
				whenRegistered: formWhenRegistered,
				isAutomaticApprovalEnabled: formIsAutomaticApprovalEnabled,
				isTokenFlowEnabled: formIsTokenFlowEnabled,
				whitelistedIPs: formWhitelistedIPs || '',
				blacklistedIPs: formBlacklistedIPs || null,
				nonRegisteredURL: formNonRegisteredURL || null,
				isLicensingEnabled: formIsLicensingEnabled,
				licenseBackendURL: formLicenseBackendURL.trim().replace(/\/+$/, '') || null,
				isSeatManagementEnabled: formIsSeatManagementEnabled,
				licenseDisplayName: formLicenseDisplayName.trim() || null,
				licensePurchaseURL: formLicensePurchaseURL.trim() || null,
				downloadGroups: [
					/* A mapping with either half blank names nothing. */
					...formDownloadGroups
						.filter((group) => group.key.trim() && group.name.trim())
						.map((group) => ({ key: group.key.trim(), name: group.name.trim() })),
					/* The ungrouped bucket last, under the reserved empty key. Omitted
					   entirely when unnamed, so the default stands. */
					...(formUngroupedName.trim() ? [{ key: '', name: formUngroupedName.trim() }] : [])
				],
				/* Drop the rows an admin started and abandoned — a link with no
				   destination is not a link. */
				documentationLinks: formDocumentationLinks
					.filter((link) => link.name.trim() && link.url.trim())
					.map(
						(link): ApplicationLink => ({
							name: link.name.trim(),
							icon: link.icon.trim() || null,
							description: link.description.trim() || null,
							url: link.url.trim()
						})
					)
			});
			toastSuccess(t2('Application updated successfully.'));
			push('/Applications');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to update application'));
		}
		isSaving = false;
	}

	// ── Logo / Icon uploads ───────────────────────────────────────

	/** Handle logo file selection and upload. */
	async function handleLogoUpload(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		/* Skip if no file was selected or there is no app context. */
		if (!file || !app) return;

		uploadingLogo = true;
		/* Upload the logo and update the local app reference on success. */
		try {
			const updated = await uploadApplicationLogo(app.id, file);
			app = { ...app, logoImageName: updated.logoImageName };
			toastSuccess(t2('Logo uploaded.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to upload logo'));
		} finally {
			uploadingLogo = false;
			input.value = '';
		}
	}

	/** Remove the logo from the application. */
	async function handleLogoDelete(): Promise<void> {
		if (!app) return;
		/* Delete the logo from the server and clear the local reference. */
		try {
			const updated = await deleteApplicationLogo(app.id);
			app = { ...app, logoImageName: updated.logoImageName };
			toastSuccess(t2('Logo removed.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete logo'));
		}
	}

	/** Handle icon file selection and upload. */
	async function handleIconUpload(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		/* Skip if no file was selected or there is no app context. */
		if (!file || !app) return;

		uploadingIcon = true;
		/* Upload the icon and update the local app reference on success. */
		try {
			const updated = await uploadApplicationIcon(app.id, file);
			app = { ...app, iconImageName: updated.iconImageName };
			toastSuccess(t2('Icon uploaded.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to upload icon'));
		} finally {
			uploadingIcon = false;
			input.value = '';
		}
	}

	/** Remove the icon from the application. */
	async function handleIconDelete(): Promise<void> {
		if (!app) return;
		/* Delete the icon from the server and clear the local reference. */
		try {
			const updated = await deleteApplicationIcon(app.id);
			app = { ...app, iconImageName: updated.iconImageName };
			toastSuccess(t2('Icon removed.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete icon'));
		}
	}

	// ── Helpers ───────────────────────────────────────────────────

	/** Format a date string for display. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	onMount(loadApplication);
</script>

<PageHeader title="{$t('Edit Application')}{app ? ` \u2014 ${app.applicationName}` : ''}" description="" />

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error}
	<div class="py-12 text-center">
		<i class="fa-solid fa-exclamation-triangle mb-4 text-4xl text-red-500"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" onclick={() => push('/Applications')}>{$t("Back to Applications")}</Button>
	</div>
{:else if app}
	<!-- ═══ HERO HEADER ═══════════════════════════════════════════ -->
	<div class="relative mb-8 overflow-hidden rounded-2xl">
		<!-- Gradient background — indigo accent to distinguish from the user page -->
		<div
			class="absolute inset-0 bg-gradient-to-r from-indigo-600 to-violet-800 dark:from-indigo-900 dark:to-gray-900"
		></div>
		<div
			class="absolute inset-0 bg-repeat opacity-10"
			style="background-image: url('/AbstractID-Tile-Dark.png'); background-size: 80px"
		></div>

		<div class="relative px-6 py-8 sm:px-8">
			<div class="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
				<!-- App logo/icon -->
				{#if app.logoImageName}
					<div class="h-20 w-20 shrink-0 rounded-xl ring-4 ring-white/20">
						<img
							src="{apiBase}/uploads/applications/{app.logoImageName}"
							alt={app.applicationName}
							class="h-full w-full rounded-xl object-contain invert"
						/>
					</div>
				{:else if app.iconImageName}
					<div class="h-16 w-16 shrink-0 rounded-xl ring-4 ring-white/20">
						<img
							src="{apiBase}/uploads/applications/{app.iconImageName}"
							alt={app.applicationName}
							class="h-full w-full rounded-xl object-contain invert"
						/>
					</div>
				{:else}
					<div
						class="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-4 ring-white/20"
					>
						<i class="fa-solid {app.icon || 'fa-cube'} text-3xl text-white/80"></i>
					</div>
				{/if}

				<!-- Name + meta -->
				<div class="min-w-0 flex-1">
					<h1 class="text-2xl font-bold leading-tight text-white sm:text-3xl">
						{app.applicationName}
					</h1>
					{#if app.description}
						<p class="mt-1 text-sm text-indigo-100">{app.description}</p>
					{/if}

					<!-- Badges -->
					<div class="mt-3 flex flex-wrap gap-1.5">
						{#if app.isActive}
							<Badge color="green" class="border border-green-400/30 !bg-green-500/20 !text-green-200"
								>{$t("Active")}</Badge
							>
						{:else}
							<Badge color="red" class="border border-red-400/30 !bg-red-500/20 !text-red-200"
								>{$t("Inactive")}</Badge
							>
						{/if}
						{#if app.isAlwaysVisible}
							<Badge color="blue" class="border border-blue-400/30 !bg-blue-500/20 !text-blue-200"
								>{$t("Always Visible")}</Badge
							>
						{/if}
						{#if app.isAutomaticApprovalEnabled}
							<Badge color="purple" class="border border-purple-400/30 !bg-purple-500/20 !text-purple-200"
								>{$t("Auto-Approve")}</Badge
							>
						{/if}
						{#if app.isTokenFlowEnabled}
							<Badge color="yellow" class="border border-yellow-400/30 !bg-yellow-500/20 !text-yellow-200"
								>{$t("Token Flow")}</Badge
							>
						{/if}
						{#if app.isOAuthEnabled}
							<Badge color="indigo" class="border border-indigo-400/30 !bg-indigo-500/20 !text-indigo-200"
								>{$t("OAuth")}</Badge
							>
						{/if}
					</div>
				</div>

				<!-- Quick stats -->
				<div class="hidden shrink-0 flex-col items-end gap-1 text-right sm:flex">
					<div class="space-y-0.5 text-xs text-indigo-200/60">
						<p>{$t("ID")} <span class="font-mono text-indigo-100">{app.id}</span></p>
						<p>{$t("Created")} {formatDate(app.created)}</p>
						{#if app.userCount !== undefined}
							<p>{$t("{{count}} registered users", { count: app.userCount })}</p>
						{/if}
					</div>
					<p class="mt-1 max-w-[240px] break-all font-mono text-[10px] text-indigo-300/40 select-all">
						{app.applicationUUID}
					</p>
				</div>
			</div>
		</div>
	</div>

	<!-- ═══ MAIN LAYOUT: 2-column ═════════════════════════════════ -->
	<div class="grid grid-cols-1 gap-8 xl:grid-cols-5">
		<!-- ─── LEFT: Form fields (wider) ────────────────────────── -->
		<div class="space-y-8 xl:col-span-3">
			<!-- Basic Information -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-cube text-primary-500"></i>
					{$t("Basic Information")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-5">
						<div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
							<div>
								<Label class="mb-1">{$t("Application Name")} *</Label>
								<Input bind:value={formName} placeholder={$t("My Application")} />
							</div>
							<div>
								<Label class="mb-1">{$t("Icon (Font Awesome class)")}</Label>
								<Input bind:value={formIcon} placeholder="fa-globe">
									{#snippet left()}
										<i class="fa-solid {formIcon || 'fa-cube'} text-gray-400"></i>
									{/snippet}
								</Input>
							</div>
						</div>
						<div>
							<Label class="mb-1">{$t("Description")}</Label>
							<Textarea
								bind:value={formDescription}
								rows={2}
								placeholder={$t("Brief description of the application...")}
								class="w-full"
							/>
						</div>
						<div>
							<Label class="mb-1">{$t("Website")}</Label>
							<Input bind:value={formWebsite} placeholder="https://..." />
						</div>
					</div>
				</div>
			</section>

			<!-- Authorization URLs -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-link text-primary-500"></i>
					{$t("Authorization URLs")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-5">
						<div>
							<Label class="mb-1">{$t("Verification URLs")}</Label>
							<Textarea
								bind:value={formVerificationURLs}
								rows={3}
								placeholder="One URL per line&#10;https://myapp.com&#10;http://localhost:3000"
								class="w-full"
							/>
							<p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
								{$t("Allowed redirect domains for authorization. One URL per line.")}
							</p>
						</div>
						<div>
							<Label class="mb-1">{$t("Non-Registered Redirect URL")}</Label>
							<Input bind:value={formNonRegisteredURL} placeholder="https://..." />
						</div>
					</div>
				</div>
			</section>

			<!-- IP Restrictions -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-shield-halved text-primary-500"></i>
					{$t("IP Restrictions")}
				</h3>
				<div class="widget p-6">
					<div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
						<div>
							<Label class="mb-1">{$t("Whitelisted IPs")}</Label>
							<Input bind:value={formWhitelistedIPs} placeholder={$t("Comma-separated IPs or CIDRs")} />
						</div>
						<div>
							<Label class="mb-1">{$t("Blacklisted IPs")}</Label>
							<Input bind:value={formBlacklistedIPs} placeholder={$t("Comma-separated IPs or CIDRs")} />
						</div>
					</div>
				</div>
			</section>

	</div>

		<!-- ─── RIGHT: Status, Media, Preview ────────────────────── -->
		<div class="space-y-8 xl:col-span-2">
			<!-- Status & Features -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-sliders text-primary-500"></i>
					{$t("Status & Features")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-4">
						<Toggle bind:checked={formIsActive}>{$t("Active")}</Toggle>
						<Toggle bind:checked={formIsAlwaysVisible}>{$t("Always Visible")}</Toggle>
						<Toggle bind:checked={formWhenRegistered}>{$t("Visible When Registered")}</Toggle>
						<Toggle bind:checked={formIsAutomaticApprovalEnabled}>{$t("Auto-Approve Users")}</Toggle>
						<Toggle bind:checked={formIsTokenFlowEnabled}>{$t("Token Flow")}</Toggle>
					</div>
				</div>
			</section>

			<!-- Licensing -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-key text-primary-500"></i>
					{$t("Licensing")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-4">
						<Toggle bind:checked={formIsLicensingEnabled}>{$t("Licensing Backend")}</Toggle>
						<p class="text-xs text-gray-500 dark:text-gray-400">
							{$t("Lets users see this product's licenses and activations from their Abstract ID account. Abstract ID stores no license data — it reads the backend live, as the signed-in user.")}
						</p>

						{#if formIsLicensingEnabled}
							<FormField
								label={$t("Backend URL")}
								helper={$t("API base including the version prefix, e.g. https://license.example.com/api/v1")}
								bind:value={formLicenseBackendURL}
							/>
							<Toggle bind:checked={formIsSeatManagementEnabled}>{$t("Allow seat re-assignment")}</Toggle>
							<p class="text-xs text-gray-500 dark:text-gray-400">
								{$t("The backend still decides per license whether its owner may delegate seats; this only hides the controls for the whole product.")}
							</p>

							<FormField
								label={$t("Display Name")}
								helper={$t("What customers call this product. Used across the license pages; blank uses the application name.")}
								bind:value={formLicenseDisplayName}
							/>

							<FormField
								label={$t("Get a License URL")}
								helper={$t("Offered to users who hold no license for this product. Blank makes no offer.")}
								bind:value={formLicensePurchaseURL}
							/>

							<div>
								<Label class="mb-2">{$t("Download Groups")}</Label>
								<p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
									{$t("Names the groups the licensing backend files builds under. Order here is the order they appear; an unmapped group still shows, under its raw identifier.")}
								</p>

								<div class="space-y-2">
									{#each formDownloadGroups as group, index (index)}
										<!-- Same narrow column, same treatment: the identifier and its name
										     stack, and the remove control sits in the header where it plainly
										     belongs to the pair. -->
										<div class="bg-depth rounded-lg p-3">
											<div class="mb-2 flex items-center justify-between gap-2">
												<span class="card-label">{$t("Group {{number}}", { number: index + 1 })}</span>
												<Button
													size="xs"
													color="alternative"
													class="!p-1.5"
													aria-label={$t("Remove group")}
													onclick={() => (formDownloadGroups = formDownloadGroups.filter((_, i) => i !== index))}
												>
													<i class="fa-solid fa-xmark text-xs"></i>
												</Button>
											</div>
											<div class="space-y-2">
												<Input placeholder="instalod-studio" bind:value={group.key} />
												<Input placeholder="InstaLOD Studio" bind:value={group.name} />
											</div>
										</div>
									{/each}
								</div>

								<Button
									size="sm"
									color="alternative"
									class="mt-2"
									onclick={() => (formDownloadGroups = [...formDownloadGroups, { key: '', name: '' }])}
								>
									<i class="fa-solid fa-plus me-2"></i>{$t("Add group")}
								</Button>

								<div class="mt-3">
									<FormField
										label={$t("Name for ungrouped builds")}
										helper={$t("Heading for builds the backend files under no group. Blank uses “Other”.")}
										bind:value={formUngroupedName}
									/>
								</div>
							</div>

							<div>
								<Label class="mb-2">{$t("Documentation Links")}</Label>
								<p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
									{$t("Shown below the user's licenses for this product, and linked from its card on the dashboard.")}
								</p>

								<div class="space-y-2">
									{#each formDocumentationLinks as link, index (index)}
										<!-- Stacked, with the remove control on its own header row. This
										     panel lives in the page's narrow right-hand column, so a row of
										     inputs plus a button wraps onto three lines and leaves the ✕
										     floating between two fields with nothing to associate it to. -->
										<div class="bg-depth rounded-lg p-3">
											<div class="mb-2 flex items-center justify-between gap-2">
												<span class="card-label">{$t("Link {{number}}", { number: index + 1 })}</span>
												<Button
													size="xs"
													color="alternative"
													class="!p-1.5"
													aria-label={$t("Remove link")}
													onclick={() => (formDocumentationLinks = formDocumentationLinks.filter((_, i) => i !== index))}
												>
													<i class="fa-solid fa-xmark text-xs"></i>
												</Button>
											</div>
											<div class="space-y-2">
												<Input placeholder={$t("Name")} bind:value={link.name} />
												<Input placeholder="fa-book" bind:value={link.icon} />
												<Input type="url" placeholder="https://docs.example.com" bind:value={link.url} />
												<Input placeholder={$t("Description")} bind:value={link.description} />
											</div>
										</div>
									{/each}
								</div>

								<Button
									size="sm"
									color="alternative"
									class="mt-2"
									onclick={() =>
										(formDocumentationLinks = [
											...formDocumentationLinks,
											{ name: '', icon: '', description: '', url: '' }
										])}
								>
									<i class="fa-solid fa-plus me-2"></i>{$t("Add link")}
								</Button>
							</div>
						{/if}
					</div>
				</div>
			</section>

			<!-- Media -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-image text-primary-500"></i>
					{$t("Media")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-5">
						<!-- Logo -->
						<div>
							<Label class="mb-2">{$t("Logo Image")}</Label>
							{#if app.logoImageName}
								<div class="mb-3 flex items-center gap-4">
									<img
										src="{apiBase}/uploads/applications/{app.logoImageName}"
										alt="Current logo"
										class="h-16 w-16 rounded-lg border object-contain dark:border-gray-600 dark:invert"
									/>
									<ConfirmButton
										size="xs"
										color="red"
										outline
										action={$t("delete this logo")}
										onconfirm={handleLogoDelete}
									>
										<i class="fa-solid fa-trash me-1"></i>{$t("Remove")}
									</ConfirmButton>
								</div>
							{/if}
							<div class="flex items-center gap-2">
								<input
									id="app-logo-input"
									type="file"
									accept="image/*"
									class="hidden"
									onchange={handleLogoUpload}
									disabled={uploadingLogo}
								/>
								<Button
									size="sm"
									color="alternative"
									disabled={uploadingLogo}
									onclick={() => document.getElementById('app-logo-input')?.click()}
								>
									{#if uploadingLogo}
										<Spinner size="4" class="mr-2" />{$t("Uploading…")}
									{:else}
										<i class="fa-solid fa-upload me-2"></i>{$t("Upload Logo")}
									{/if}
								</Button>
								<span class="text-xs text-gray-500 dark:text-gray-400">{$t("Resized to 512×512")}</span>
							</div>
						</div>

						<!-- Icon -->
						<div class="border-t border-gray-200 pt-4 dark:border-gray-700">
							<Label class="mb-2">{$t("Icon Image")}</Label>
							{#if app.iconImageName}
								<div class="mb-3 flex items-center gap-4">
									<img
										src="{apiBase}/uploads/applications/{app.iconImageName}"
										alt="Current icon"
										class="h-10 w-10 rounded border object-contain dark:border-gray-600 dark:invert"
									/>
									<ConfirmButton
										size="xs"
										color="red"
										outline
										action={$t("delete this icon")}
										onconfirm={handleIconDelete}
									>
										<i class="fa-solid fa-trash me-1"></i>{$t("Remove")}
									</ConfirmButton>
								</div>
							{/if}
							<div class="flex items-center gap-2">
								<input
									id="app-icon-input"
									type="file"
									accept="image/*"
									class="hidden"
									onchange={handleIconUpload}
									disabled={uploadingIcon}
								/>
								<Button
									size="sm"
									color="alternative"
									disabled={uploadingIcon}
									onclick={() => document.getElementById('app-icon-input')?.click()}
								>
									{#if uploadingIcon}
										<Spinner size="4" class="mr-2" />{$t("Uploading…")}
									{:else}
										<i class="fa-solid fa-upload me-2"></i>{$t("Upload Icon")}
									{/if}
								</Button>
								<span class="text-xs text-gray-500 dark:text-gray-400">{$t("Resized to 128×128")}</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Preview & Credentials -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-key text-primary-500"></i>
					{$t("Credentials & Preview")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-3 text-sm">
						<div>
							<span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
								>{$t("UUID")}</span
							>
							<code
								class="bg-depth mt-0.5 block break-all rounded px-2 py-1 text-xs select-all"
								>{app.applicationUUID}</code
							>
						</div>
						<div>
							<span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
								>{$t("Private Key")}</span
							>
							<code
								class="bg-depth mt-0.5 block truncate rounded px-2 py-1 text-xs select-all"
								>{app.privateKey}</code
							>
						</div>
						<div>
							<span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
								>{$t("Created")}</span
							>
							<p class="mt-0.5 text-gray-700 dark:text-gray-300">{formatDate(app.created)}</p>
						</div>
					</div>

					<div class="mt-5 flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
						<Button
							size="sm"
							color="light"
							onclick={() =>
								window.open(
									`/authorize?app=${app?.applicationUUID}&previewConsent=true`,
									'_blank'
								)}
						>
							<i class="fa-solid fa-eye me-2"></i>{$t("Preview Consent")}
						</Button>
						{#if app.isTokenFlowEnabled}
							<Button
								size="sm"
								color="light"
								onclick={() =>
									window.open(
										`/authorize?app=${app?.applicationUUID}&returnURL=token&previewConsent=true`,
										'_blank'
									)}
							>
								<i class="fa-solid fa-clipboard me-2"></i>{$t("Preview Token Flow")}
							</Button>
						{/if}
					</div>
				</div>
			</section>
		</div>
	</div>

	<!-- Sticky Save Bar -->
	<div
		class="sticky bottom-4 z-10 mt-8 flex justify-end gap-3 rounded-xl border border-gray-200 bg-zinc-100/90 px-6 py-4 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-zinc-800/90"
	>
		<Button color="alternative" onclick={() => push('/Applications')}>{$t("Cancel")}</Button>
		<Button color="primary" onclick={handleSave} disabled={isSaving}>
			{#if isSaving}<Spinner size="4" class="mr-2" />{/if}
			{$t("Save Changes")}
		</Button>
	</div>
{/if}